import {AsyncPipe} from '@angular/common';
import {Component, forwardRef, inject, Injector, input, OnInit} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, Validator, Validators, NgControl, FormControlName, FormGroupDirective, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {Observable, of, Subscription} from 'rxjs';
import {catchError, debounceTime, map, startWith, switchMap} from 'rxjs/operators';
import {DynamicAutocompleteItem, DynamicAutocompleteResponse, DynamicAutocompleteService} from '../../../shared/service/dynamic-autocomplete/dynamic-autocomplete.service';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {autocompleteModelValidator} from 'src/infrastructure/helper/autocomplete-model-validator';

@AutoUnsubscribe()
@Component({
    selector: 'reconciliation-autocomplete',
    template: `
    <mat-form-field appearance="outline">
      <mat-label>{{ formLabel() }}</mat-label>
      <input 
        matInput 
        type="text"
        [formControl]="autocompleteInnerControl"
        [matAutocomplete]="autocomplete"
        [attr.data-testid]="dataTestId() + '-input'"
        placeholder="Type to search...">
      
      @if (!hideClearButton() && autocompleteInnerControl.value) {
        <button 
          matSuffix 
          mat-icon-button 
          type="button"
          [attr.data-testid]="dataTestId() + '-clear-button'"
          (click)="clearAutocomplete(autocomplete)">
          <mat-icon>close</mat-icon>
        </button>
      }
      
      <mat-autocomplete 
        #autocomplete="matAutocomplete"
        [displayWith]="displayNameWith"
        (optionSelected)="setValue($event.option.value)"
        [attr.data-testid]="dataTestId() + '-autocomplete'">
        @for (option of filteredOptions$ | async; track option.id) {
          <mat-option 
            [value]="option" 
            [attr.data-testid]="dataTestId() + '-option'">
            {{ displayNameWith(option) }}
          </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  `,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        MatButtonModule,
        AsyncPipe,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReconciliationAutocompleteComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ReconciliationAutocompleteComponent),
            multi: true,
        },
    ]
})
export class ReconciliationAutocompleteComponent implements OnInit, ControlValueAccessor, Validator {
    public formLabel = input<string>('Autocomplete');
    public hideClearButton = input<boolean>(false);
    public dataTestId = input<string>('data-test-id');
    public maxResults = input<number>(25);
    public emitString = input<boolean>(false);
    // Force ReconciliationView/AdxReference
    public tableName = input<string>('ReconciliationView');
    public columnName = input<string>('AdxReference');

    private readonly injector = inject(Injector);
    private readonly autocompleteService = inject(DynamicAutocompleteService);

    protected autocompleteInnerControl = new FormControl<DynamicAutocompleteItem | string | null>(null);

    protected options: DynamicAutocompleteItem[] = [];
    protected filteredOptions$?: Observable<DynamicAutocompleteItem[]>;
    protected inputTextEmit$?: Subscription;
    
    protected displayNameWith = (option: DynamicAutocompleteItem): string => 
        option ? option.value : '';
    
    private onChange!:(value: DynamicAutocompleteItem | string | null) => void;
    private onTouched!:(value: DynamicAutocompleteItem | string | null) => void;

    public ngOnInit(): void {
        this.filteredOptions$ = this.autocompleteInnerControl.valueChanges
        .pipe(
            startWith(''),
            debounceTime(200),
            switchMap(value => {
                if(value && typeof value === 'string'){
                    const apiParams = {
                        tableName: 'ReconciliationView',
                        columnName: 'AdxReference',
                        userInput: value,
                        maxResults: this.maxResults(),
                    };
                    
                    return this.autocompleteService.getOptions(apiParams)
                    .pipe(
                        map((data: DynamicAutocompleteResponse) => {
                            this.options = data.items;
                            return this.filterOptions(value || '');
                        }),
                        catchError((error: unknown) => {
                            console.error('Error in reconciliation autocomplete:', error);
                            return of([] as DynamicAutocompleteItem[]);
                        })
                    );
                }
                this.options = [];
                return of([]);
            }),
        );
        
        this.inputTextEmit$ = this.autocompleteInnerControl.valueChanges.subscribe((event) => {
            if(this.emitString() && ((typeof event === 'string' && event?.length >= 1) || !event)) {
                this.setValue(event ? {value: `${event}`} : null);
            }
        });

        const parentControl = this.injector.get(NgControl) as FormControlName;
        const parentForm = this.injector.get(FormGroupDirective).getControl(parentControl);

        if(parentForm.hasValidator(Validators.required)){
            this.autocompleteInnerControl.setValidators([autocompleteModelValidator() , Validators.required]);
        }
    }

    private filterOptions(value: string): DynamicAutocompleteItem[] {
        if (!value) return this.options;
        
        const filterValue = value.toLowerCase().trim();
        if (!filterValue) return this.options;
        
        return this.options.filter(
            option => option.value && option.value.toLowerCase().includes(filterValue)
        ) as DynamicAutocompleteItem[];  
    }

    protected clearAutocomplete(componentRef: MatAutocomplete): void {
        componentRef.options.forEach((optionRef) => optionRef.deselect());
        this.options = [];
        this.autocompleteInnerControl.reset(null);
        this.onChange(null);
        this.onTouched(null);
    }

    public writeValue(value: DynamicAutocompleteItem | string | null): void {
        if (value && typeof value === 'string') {
            // When the value is a string (e.g., from a restored filter), 
            // create a proper DynamicAutocompleteItem object for display
            const autocompleteItem: DynamicAutocompleteItem = {
                value: value,
                originalValue: value
            };
            this.autocompleteInnerControl.patchValue(autocompleteItem);
        } else {
            this.autocompleteInnerControl.patchValue(value);
        }
        
        if (!value) {
            this.autocompleteInnerControl.reset();
        }
    }

    public registerOnChange(fn: (value: DynamicAutocompleteItem | string | null)=> void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: (value: DynamicAutocompleteItem | string | null)=> void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.autocompleteInnerControl[isDisabled? 'disable' : 'enable']();
    }

    public validate(control: AbstractControl): ValidationErrors | null {
        if(control.errors && !control.value){
            return {
                required: true,
            };
        }
        return null;
    }

    public setValue(value:DynamicAutocompleteItem | string | null): void {
        this.onChange(value);
        this.onTouched(value);
    }
}
